<script setup lang="ts">
import type { TrainingFormat } from "@simbelapp/order-sdk";
import CenterModal from "~/components/Common/Modals/CenterModal.vue";
import TabNavigationSimple from "~/components/Common/Navigations/TabNavigationSimple.vue";
import AppButton from "~/components/Common/Buttons/AppButton.vue";
import LoadButton from "~/components/Common/Buttons/LoadButton.vue";
import SvgIcon from "~/components/Common/SvgIcon.vue";
import EditTimeslotModalConfirm from "~/components/Pages/training-plan/session-details/tabs/admin/timeslots/session-timeslots-modal/EditTimeslotModalConfirm.vue";
import { useAddSessionFlow } from "~/composables/trainings/useAddSessionFlow";
import { AddSessionTabEnum, AddSessionTabLabel, TimeslotConfirmModeEnum } from "~/utils/enums/sessions.enums";
import { useAuthStore } from "~/store/auth/auth.store";
import AddSessionGeneralTab from "./AddSessionGeneralTab.vue";
import AddSessionStepsTab from "./AddSessionStepsTab.vue";

const TABS = [
  { name: AddSessionTabLabel[AddSessionTabEnum.GENERAL], tab: AddSessionTabEnum.GENERAL },
  { name: AddSessionTabLabel[AddSessionTabEnum.STEPS], tab: AddSessionTabEnum.STEPS },
];

/**
 * « Ajouter une session » for a catalogue training, external or internal alike: the Sessions tab
 * that opened it publishes its own store slice and actions, so there is one wizard rather than
 * one per modal.
 */
const flow = useAddSessionFlow();
const authStore = useAuthStore();

const activeTab = ref<AddSessionTabEnum>(AddSessionTabEnum.GENERAL);
const activeTabLabel = computed(() => AddSessionTabLabel[activeTab.value]);
const isSaving = ref(false);
/** The steps inherit their format and capacity from it, so it gates the whole second tab. */
const isInscriptionTypeChosen = computed(() => !!flow.sessions.sessionDraft?.sessionItemType);
/** A session carries its dates through its steps: without one there is nothing to create. */
const missingTimeslot = ref(false);
/** The first step form is opened on arrival, then left alone so tab switches keep the input. */
const hasOpenedFirstStep = ref(false);

/** Hours left to spread over the steps, so a fresh one starts pre-filled with the remainder. */
const remainingDurationHours = computed(() => {
  const booked = (flow.sessions.sessionDraft?.sessionTimeslot ?? []).reduce(
    (sum, timeslot) => sum + timeslot.trainingDurationHours,
    0,
  );
  return Math.max(flow.training.durationHours - booked, 0);
});

function newTimeslot() {
  const draft = flow.sessions.sessionDraft;
  if (!draft) return;

  flow.sessions.editTimeslot = null;
  draft.currentSessionTimeslot = {
    sessionId: undefined,
    projectId: undefined,
    startDate: "",
    endDate: "",
    trainingDurationHours: remainingDurationHours.value,
    format: draft.format as TrainingFormat,
    location: "",
    name: "",
    trainersUsersId: [],
    // A new step starts on the planner's zone; the form can change it.
    timezone: authStore.userTimezone,
  };
  flow.sessions.showAddTimeslot = true;
  flow.sessions.showEditTimeslotModalConfirm = null;
  missingTimeslot.value = false;
}

/** Adding a step while another is half-edited would drop the edit: ask first. */
function requestNewTimeslot() {
  if (flow.sessions.editTimeslot !== null || flow.sessions.showAddTimeslot) {
    flow.sessions.showEditTimeslotModalConfirm = TimeslotConfirmModeEnum.CREATE;
  } else {
    newTimeslot();
  }
}

async function goToTab(tab: AddSessionTabEnum) {
  if (tab === activeTab.value) return;

  // The inscription type drives the steps' defaults, so it has to be picked before leaving step 1.
  if (tab === AddSessionTabEnum.STEPS) {
    await flow.validate();
    if (Object.keys(flow.errors).length) return;
  }

  activeTab.value = tab;

  if (tab === AddSessionTabEnum.STEPS && !hasOpenedFirstStep.value) {
    hasOpenedFirstStep.value = true;
    newTimeslot();
  }
}

function closeModal() {
  flow.close();
}

/** "Retour" walks the wizard back, and drops the draft altogether from the first step. */
function onBack() {
  if (activeTab.value === AddSessionTabEnum.STEPS) activeTab.value = AddSessionTabEnum.GENERAL;
  else closeModal();
}

async function addSession() {
  isSaving.value = true;

  try {
    if (!flow.sessions.sessionDraft?.sessionTimeslot?.length) {
      missingTimeslot.value = true;
      return;
    }

    if (flow.sessions.editTimeslot !== null || flow.sessions.showAddTimeslot) {
      flow.sessions.showEditTimeslotModalConfirm = TimeslotConfirmModeEnum.ADD_SESSION;
      return;
    }

    await flow.submit();
  } finally {
    // `nextTick` so LoadButton sees a true → false transition and stops spinning, whichever
    // branch ran — including a save that threw, which would otherwise leave it disabled.
    await nextTick();
    isSaving.value = false;
  }
}

async function confirmPendingStep() {
  if (flow.sessions.showEditTimeslotModalConfirm === TimeslotConfirmModeEnum.ADD_SESSION) {
    await flow.submit();
    // A refused session leaves the wizard open: the question must not stay up over it.
    flow.sessions.showEditTimeslotModalConfirm = null;
  } else {
    newTimeslot();
  }
}
</script>

<template>
  <CenterModal class="h-full max-h-[688px] w-full max-w-[720px]" @hide="closeModal">
    <template #body>
      <div class="flex h-full flex-col body_regular">
        <div class="flex h-12 shrink-0 items-center justify-end px-4">
          <SvgIcon
            name="close"
            class="h-6 w-6 cursor-pointer text-secondary-color"
            title="Fermer"
            @click="closeModal"
          />
        </div>

        <div class="shrink-0 px-16">
          <TabNavigationSimple :headers-nav="TABS" :tab-selected="activeTabLabel" @tab-navigate="goToTab($event.tab)" />
        </div>

        <div class="flex-1 overflow-auto px-16 py-6">
          <div v-show="activeTab === AddSessionTabEnum.GENERAL">
            <AddSessionGeneralTab />
          </div>
          <div v-show="activeTab === AddSessionTabEnum.STEPS">
            <AddSessionStepsTab
              :missing-timeslot="missingTimeslot"
              @add-step="requestNewTimeslot"
              @reset-step="newTimeslot"
            />
          </div>
        </div>

        <div class="flex shrink-0 justify-between gap-4 border-t border-stroke-color px-16 py-4">
          <AppButton type="outlined" label="Retour" class="flex-1" @click="onBack" />
          <AppButton
            v-if="activeTab === AddSessionTabEnum.GENERAL"
            label="Continuer"
            class="flex-1"
            :disabled="!isInscriptionTypeChosen"
            @click="goToTab(AddSessionTabEnum.STEPS)"
          />
          <LoadButton
            v-else
            v-model="isSaving"
            class="flex-1"
            text-confirm="Ajouter la session"
            force-waiting
            @click="addSession"
          />
        </div>
      </div>
    </template>
  </CenterModal>

  <EditTimeslotModalConfirm
    v-if="
      flow.sessions.showEditTimeslotModalConfirm === TimeslotConfirmModeEnum.CREATE ||
      flow.sessions.showEditTimeslotModalConfirm === TimeslotConfirmModeEnum.ADD_SESSION
    "
    @cancel="flow.sessions.showEditTimeslotModalConfirm = null"
    @confirm="confirmPendingStep"
  />
</template>
